import type { User, LoginCredentials, AuthResponse } from "@/types/auth";
import { t } from "@/i18n";
import { HttpError } from "@/types/http";
import { httpClient } from "@/services/api";
import { loginClient, publicClient } from "@/services/api/axiosClients";
import { setToken, clearToken } from "@/utils/authToken";

interface FrappeUserData {
  name: string;
  email: string;
  full_name: string;
  first_name: string;
  username: string;
  iot_company: string;
  iot_site_administrator: number;
  iot_site_read_only: number;
  language: string;
  time_zone: string;
  phone?: string;
  [key: string]: any;
}

interface ApiKeysResponse {
  message: {
    api_key: string;
    api_secret: string;
  };
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      if (!credentials.email || !credentials.password) {
        return { success: false, message: t("auth.errors.credentialsRequired") };
      }

      // Step 1: obter o token de API (api_key:api_secret) via endpoint custom
      // do app `max`. Diferente de /api/method/login, não cria sessão por
      // cookie — o token é permanente e vai no header Authorization.
      const keysRes = await loginClient.post<ApiKeysResponse>(
        "/api/method/max.api.get_user_api_keys.get_user_api_keys",
        {
          usr: credentials.email,
          pwd: credentials.password,
        },
      );
      const keys = keysRes.data?.message;

      if (!keys?.api_key || !keys?.api_secret) {
        return {
          success: false,
          message: t("auth.errors.invalidCredentials"),
        };
      }

      // Persiste o token; a partir daqui os clientes HTTP injetam
      // `Authorization: token api_key:api_secret` automaticamente.
      const token = `${keys.api_key}:${keys.api_secret}`;
      setToken(token);

      // Step 2: buscar o registro completo do usuário. Com token não há a
      // "race" de registro de sessão do modelo de cookie, então uma única
      // chamada basta (mantemos o fallback defensivo abaixo).
      let userData: FrappeUserData | null = null;
      try {
        userData = await httpClient.get<FrappeUserData>(
          `/api/resource/User/${encodeURIComponent(credentials.email)}`,
        );
      } catch (error) {
        console.warn(
          "[authService.login] ⚠️ Falha ao buscar dados do usuário, usando fallback:",
          error,
        );
      }

      // Fallback: sintetiza um registro mínimo a partir do e-mail informado.
      if (!userData) {
        userData = {
          name: credentials.email,
          email: credentials.email,
          full_name: credentials.email,
          first_name: credentials.email.split(" ")[0],
          username: credentials.email,
          iot_company: "",
          iot_site_administrator: 0,
          iot_site_read_only: 0,
          language: "pt",
          time_zone: "America/Sao_Paulo",
        };
      }

      const isAdmin = userData.iot_site_administrator === 1;
      const isReadOnly = userData.iot_site_read_only === 1;
      const role: User["role"] = isAdmin
        ? "admin"
        : isReadOnly
          ? "viewer"
          : "operator";

      const user: User = {
        id: userData.name,
        name:
          userData.full_name ||
          userData.first_name ||
          userData.username ||
          "Usuário",
        email: userData.email,
        role,
        avatar: null,
        createdAt: new Date(),
        lastLogin: new Date(),
        username: userData.username,
        iotCompany: userData.iot_company,
        isAdmin,
        isReadOnly,
      };
      if (userData.phone) {
        user.phoneNumber = userData.phone;
      }

      return {
        success: true,
        user,
        token,
        expiresIn: 3600,
        message: t("auth.loginSuccess"),
      };
    } catch (error) {
      // Limpa qualquer token órfão se o fluxo de login falhar de forma fatal.
      clearToken();
      console.error("[authService.login] Erro no login:", error);
      if (error instanceof HttpError) {
        const code = error.status;
        const desc = error.message || error.statusText || "Erro";
        return {
          success: false,
          message: code ? `${code} - ${desc}` : desc,
        };
      }
      return {
        success: false,
        message:
          error instanceof Error ? error.message : t("auth.errors.serverError"),
      };
    }
  },

  async logout(): Promise<void> {
    try {
      await httpClient.post("/api/method/logout", {});
    } catch (error) {
      console.warn(
        "[authService.logout] Erro ao chamar logout no servidor (ignorado):",
        error,
      );
    }
  },

  async validateToken(): Promise<boolean> {
    try {
      // get_logged_user retorna { message: "<email>" } (ou "Guest" se o token
      // for inválido/revogado). Não tem `.data`, então o httpClient não
      // desembrulha — lemos res.message diretamente.
      const res = await httpClient.get<{ message: string }>(
        "/api/method/frappe.auth.get_logged_user",
      );
      const email = res?.message;
      return !!email && email !== "Guest";
    } catch (error) {
      console.error(
        "[authService.validateToken] Erro ao validar sessão:",
        error,
      );
      return false;
    }
  },

  async forgotPassword(
    email: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const res = await publicClient.post<{ message: string }>(
        "/api/method/frappe.core.doctype.user.user.reset_password",
        { user: email },
      );
      return {
        success: true,
        message:
          res.data?.message ||
          "Instruções de recuperação foram enviadas para seu e-mail",
      };
    } catch (error) {
      console.error("Erro ao solicitar recuperação de senha:", error);
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : t("auth.errors.resetRequestFailed"),
      };
    }
  },

  async updatePassword(
    key: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const res = await publicClient.post<{
        message?: string;
        home_page?: string;
        full_name?: string;
      }>("/api/method/frappe.core.doctype.user.user.update_password", {
        key,
        new_password: newPassword,
      });

      const body = res.data || {};
      const isSuccess =
        body.home_page !== undefined || body.full_name !== undefined;

      return {
        success: true,
        message: isSuccess
          ? t("auth.passwordUpdated")
          : body.message ||
            "Senha atualizada com sucesso. Redirecionando para login...",
      };
    } catch (error) {
      console.error("Erro ao atualizar senha:", error);
      return {
        success: false,
        message:
          error instanceof Error ? error.message : t("auth.errors.passwordUpdateFailed"),
      };
    }
  },
};
